/**
 * Offering a feature to somebody, and the offers waiting for you.
 *
 * Shared between the account page (your own features, your own offers) and the
 * admin page (a super-admin passing somebody else's feature on), because the
 * thing being asked is identical and the warnings that go with it — this is an
 * offer, not a copy; a logic module means somebody else's code — must not
 * drift between the two places they are shown.
 */

import { useCallback, useState, type FormEvent, type ReactNode } from "react";
import Alert from "react-bootstrap/Alert";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import type { Feature, FeatureShare } from "@xtract-bot/embellisher-schema";
import { api, errorMessage } from "../api";
import { EmptyState, TimeAgo, ViewerModal } from "./ui";

/* ── Making an offer ──────────────────────────────────────────────────────── */

export interface ShareModalProps {
  /** The feature being offered, or null when the modal is closed. */
  feature: Feature | null;
  /** Whose feature it is, when that is not the person doing the offering. */
  onBehalfOf?: string;
  /** How to send it — the account route, or the admin one. */
  send: (featureId: string, toEmail: string, note: string | undefined) => Promise<FeatureShare>;
  onClose: () => void;
  onShared: (share: FeatureShare) => void;
}

export function ShareFeatureModal({ feature, onBehalfOf, send, onClose, onShared }: ShareModalProps): ReactNode {
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = useCallback(() => {
    setEmail("");
    setNote("");
    setError(null);
    onClose();
  }, [onClose]);

  const submit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      if (!feature || !email.trim()) return;
      setBusy(true);
      setError(null);
      try {
        const share = await send(feature.id, email.trim(), note.trim() || undefined);
        onShared(share);
        close();
      } catch (cause) {
        setError(errorMessage(cause));
      } finally {
        setBusy(false);
      }
    },
    [feature, email, note, send, onShared, close],
  );

  return (
    <Modal show={feature !== null} onHide={close} centered>
      <Form onSubmit={(event) => void submit(event)}>
        <Modal.Header closeButton>
          <div>
            <Modal.Title as="h2" className="h5 mb-0">
              Offer “{feature?.name}”
            </Modal.Title>
            <div className="emb-muted small">{onBehalfOf ? `From ${onBehalfOf}'s account` : "From your account"}</div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3" controlId="emb-share-email">
            <Form.Label className="small emb-muted">Their email address</Form.Label>
            <Form.Control
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="someone@example.com"
              autoComplete="off"
              autoFocus
            />
            <Form.Text className="emb-muted">
              It does not need an account yet — the offer waits until they sign in with that address.
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3" controlId="emb-share-note">
            <Form.Label className="small emb-muted">A line about why (optional)</Form.Label>
            <Form.Control value={note} onChange={(event) => setNote(event.target.value)} placeholder="This is what I use on the order pages" />
          </Form.Group>
          <p className="emb-muted small mb-0">
            Nothing is emailed and nothing is copied yet. The offer appears in their extension and account page, and the
            feature reaches their browsers only if they accept it. What you send is a snapshot: editing this feature
            afterwards will not change what they were offered.
          </p>
          {feature?.logic ? (
            <Alert variant="warning" className="py-2 small mt-3 mb-0">
              This feature runs the logic module <code>{feature.logic.moduleId}</code>. Accepting copies that module into
              their account, so compiled code from this account would run in their browser.
            </Alert>
          ) : null}
          {error ? (
            <Alert variant="danger" className="py-2 small mt-3 mb-0">
              {error}
            </Alert>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={busy || email.trim().length === 0}>
            {busy ? "Offering…" : "Offer it"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

/* ── Answering one ────────────────────────────────────────────────────────── */

function OfferCard({ share, onChanged }: { share: FeatureShare; onChanged: () => void }): ReactNode {
  const [busy, setBusy] = useState<"accept" | "decline" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [viewing, setViewing] = useState(false);

  const answer = useCallback(
    async (choice: "accept" | "decline") => {
      setBusy(choice);
      setError(null);
      try {
        if (choice === "accept") await api.acceptShare(share.shareId);
        else await api.declineShare(share.shareId);
        onChanged();
      } catch (cause) {
        setError(errorMessage(cause));
      } finally {
        setBusy(null);
      }
    },
    [share, onChanged],
  );

  return (
    <div className="emb-sunken p-3 mb-3">
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
        <div>
          <div className="fw-semibold">{share.featureName}</div>
          <div className="emb-muted small">
            from {share.from.username}
            {share.sharedBy.startsWith("admin:") ? " · sent by an administrator" : ""} · <TimeAgo iso={share.createdAt} />
          </div>
        </div>
        <Button size="sm" variant="link" className="p-0" onClick={() => setViewing(true)}>
          View JSON
        </Button>
      </div>

      {share.note ? <p className="mb-2 mt-2">“{share.note}”</p> : null}
      {share.feature.description ? <p className="emb-muted small mb-2 mt-2">{share.feature.description}</p> : null}

      <div className="d-flex flex-wrap gap-1 mb-2">
        {share.feature.match.hosts.map((host) => (
          <Badge key={host} bg="secondary">
            {host}
          </Badge>
        ))}
        {share.feature.network?.hosts.map((host) => (
          <Badge key={`net-${host}`} bg="warning" text="dark">
            may fetch {host}
          </Badge>
        ))}
      </div>

      {share.logic ? (
        <Alert variant="warning" className="py-2 small">
          Carries the WebAssembly module <code>{share.logic.moduleId}</code> ({Math.max(1, Math.round(share.logic.sizeBytes / 1024))} KB).
          Accepting copies it into your account and runs code written by {share.from.username} in your browsers. It is
          sandboxed to the effects this feature declares, but it is not your code.
        </Alert>
      ) : null}

      {error ? (
        <Alert variant="danger" className="py-2 small">
          {error}
        </Alert>
      ) : null}

      <div className="d-flex gap-2">
        <Button size="sm" variant="primary" disabled={busy !== null} onClick={() => void answer("accept")}>
          {busy === "accept" ? "Adding…" : "Add to my features"}
        </Button>
        <Button size="sm" variant="outline-secondary" disabled={busy !== null} onClick={() => void answer("decline")}>
          {busy === "decline" ? "Declining…" : "Decline"}
        </Button>
      </div>

      <ViewerModal
        show={viewing}
        title={share.featureName}
        subtitle={`Offered by ${share.from.username}`}
        body={JSON.stringify(share.feature, null, 2)}
        onHide={() => setViewing(false)}
      />
    </div>
  );
}

function statusBadge(status: FeatureShare["status"]): ReactNode {
  switch (status) {
    case "accepted":
      return <Badge bg="success">accepted</Badge>;
    case "declined":
      return <Badge bg="secondary">declined</Badge>;
    case "withdrawn":
      return <Badge bg="secondary">withdrawn</Badge>;
    default:
      return <Badge bg="primary">waiting</Badge>;
  }
}

/** Both halves of sharing, in the order they matter: what is asked of you, then what you asked of others. */
/**
 * What to say after an offer is made. Usually nothing — it is in their list
 * now — but an address the access list does not admit cannot sign in to see
 * it, and the sender has no other way to find that out.
 */
export function shareOutcomeMessage(share: FeatureShare): { text: string; warning: boolean } {
  if (share.recipientAuthorised === false) {
    return {
      text: `Offered to ${share.toEmail}, and kept — but that address is not authorised to use this deployment, so they cannot sign in to see it yet. An administrator has to add it first.`,
      warning: true,
    };
  }
  return { text: `Offered to ${share.toEmail}. It reaches them only if they accept it.`, warning: false };
}

export function SharesCard({
  incoming,
  outgoing,
  loading,
  onChanged,
}: {
  incoming: FeatureShare[];
  outgoing: FeatureShare[];
  loading: boolean;
  onChanged: () => void;
}): ReactNode {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const withdraw = useCallback(
    async (shareId: string) => {
      setBusy(shareId);
      setError(null);
      try {
        await api.withdrawShare(shareId);
        onChanged();
      } catch (cause) {
        setError(errorMessage(cause));
      } finally {
        setBusy(null);
      }
    },
    [onChanged],
  );

  if (!loading && incoming.length === 0 && outgoing.length === 0) return null;

  return (
    <Card className="mb-4">
      <Card.Header>Shared features</Card.Header>
      <Card.Body>
        {incoming.length > 0 ? (
          <>
            <h3 className="h6 mb-3">
              Offered to you <Badge bg="primary">{incoming.length}</Badge>
            </h3>
            {incoming.map((share) => (
              <OfferCard key={share.shareId} share={share} onChanged={onChanged} />
            ))}
          </>
        ) : null}

        {outgoing.length > 0 ? (
          <>
            <h3 className={`h6 mb-2 ${incoming.length > 0 ? "mt-4" : ""}`}>You offered</h3>
            {error ? (
              <Alert variant="danger" className="py-2 small">
                {error}
              </Alert>
            ) : null}
            <ul className="list-unstyled mb-0">
              {outgoing.map((share) => (
                <li key={share.shareId} className="d-flex flex-wrap align-items-baseline gap-2 py-2 border-bottom border-light-subtle">
                  <span className="fw-semibold">{share.featureName}</span>
                  <span className="emb-muted small">to {share.toEmail}</span>
                  {statusBadge(share.status)}
                  <span className="emb-muted small ms-auto">
                    <TimeAgo iso={share.resolvedAt ?? share.createdAt} />
                  </span>
                  {share.status === "pending" ? (
                    <Button
                      size="sm"
                      variant="link"
                      className="p-0 text-danger"
                      disabled={busy === share.shareId}
                      onClick={() => void withdraw(share.shareId)}
                    >
                      {busy === share.shareId ? "Withdrawing…" : "Withdraw"}
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
            {outgoing.some((share) => share.status === "accepted") ? (
              <p className="emb-muted small mb-0 mt-3">
                An accepted feature is a copy in someone else's account. Withdrawing is only possible before they take
                it — after that it is theirs, and editing yours does not change theirs.
              </p>
            ) : null}
          </>
        ) : incoming.length === 0 ? (
          <EmptyState title="Nothing shared yet">
            Use <strong>Share</strong> beside any feature below to offer it to someone by email address.
          </EmptyState>
        ) : null}
      </Card.Body>
    </Card>
  );
}
